import * as THREE from 'three';
import React, { useMemo, useRef, useEffect } from 'react';
import { correctAxisData } from '../../utils';
import { compute3DMeshDataAndPosition, loadTexture } from '../../utils';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from '../../reactThreeUtils';
import { getMaterialData } from '../../helpers';
import { useSpring, a } from 'react-spring/three';
import * as constantsViewer from '../../constants';
import textureCornerGalva from 'assets/images/textures/galva.jpg';
import textureAlu from 'assets/images/textures/alu-inox.jpg';
import textureWhiteAsh from 'assets/images/textures/whiteash.jpg';
import data from './data/doorHandle';

export function DoorSlat({
  door,
  dPosition,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  if (door.base.slatMaterial === 'W')
    return (
      <DoorSlatWood
        door={door}
        dPosition={dPosition}
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
    );
  else
    return (
      <DoorSlatAlu
        door={door}
        dPosition={dPosition}
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
    );
}

function DoorSlatWood({
  door,
  dPosition,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  const doorPosition = door.position;
  const doorInside = door.doorIsInside;
  const doorHandle = door.handle;
  var modelCabinetWidth = 999;
  var xOffset = 50;

  var modelCabinetDepth = 419;

  var modelShelfHeight = 370;

  var scaleY = shelfHeight / modelShelfHeight;
  scaleY = (370 * scaleY - 40) / 330;

  var cabinetDiffWith = cabinetWidth - modelCabinetWidth;
  var cabinetDiffDepth = cabinetDepth - modelCabinetDepth;

  var vertices = new Float32Array(data.doorSlatWood.vertices);
  var normals = new Float32Array(data.doorSlatWood.normals);
  var uv_coord = new Float32Array(data.doorSlatWood.uv_coord);

  const texture = useMemo(() => loadTexture(textureWhiteAsh), [
    textureWhiteAsh,
  ]);

  return (
    <mesh
      receiveShadow
      castShadow
      renderOrder={constantsViewer.ZORDER_HANDLE}
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);
        let size = new THREE.Vector3();
        self.geometry.boundingBox.getSize(size);
        self.position.z += cabinetDiffDepth / 2;
        self.position.y -= 4;

        self.scale.set(1, scaleY, 1);

        if (!doorInside) self.position.z += 6;

        switch (dPosition) {
          case 'FL':
            self.position.x -= modelCabinetWidth + cabinetDiffWith / 2;
            self.position.x += 2 * xOffset;
            self.position.x -= 16;
            break;

          case 'FR':
            self.position.x += cabinetDiffWith / 2;
            self.position.x += 8;
            break;

          case 'C':
            self.position.x += 62;

            self.position.x += cabinetDiffWith / 2;
            self.position.x -= cabinetWidth / 2;
            if (doorPosition == 'FL') self.position.x += 4;
            else if (doorPosition == 'FR') self.position.x -= 4;

            break;

          default:
            break;
        }
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={constantsViewer.MATERIAL_CROSSPIECE_COLOR}
        specular={constantsViewer.MATERIAL_CROSSPIECE_SPECULAR}
        shininess={constantsViewer.MATERIAL_CROSSPIECE_SHININESS}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
        map={texture}
        onUpdate={self => (self.needsUpdate = true)}
      />
    </mesh>
  );
}

function DoorSlatAlu({
  door,
  dPosition,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  const doorPosition = door.position;
  const doorInside = door.doorIsInside;
  const doorHandle = door.handle;
  var modelCabinetWidth = 999;
  var xOffset = 50;

  var modelCabinetDepth = 419;

  var modelShelfHeight = 370;

  var scaleY = shelfHeight / modelShelfHeight;
  scaleY = (370 * scaleY - 40) / 330;

  var cabinetDiffWith = cabinetWidth - modelCabinetWidth;
  var cabinetDiffDepth = cabinetDepth - modelCabinetDepth;

  var vertices = new Float32Array(data.doorSlatAlu.vertices);
  var normals = new Float32Array(data.doorSlatAlu.normals);
  var uv_coord = new Float32Array(data.doorSlatAlu.uv_coord);

  const texture = useMemo(() => loadTexture(textureAlu), [textureAlu]);

  return (
    <mesh
      receiveShadow
      castShadow
      renderOrder={constantsViewer.ZORDER_HANDLE}
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);
        let size = new THREE.Vector3();
        self.geometry.boundingBox.getSize(size);
        self.position.z += cabinetDiffDepth / 2;
        self.position.y -= 4;

        self.scale.set(1, scaleY, 1);

        if (!doorInside) self.position.z += 6;

        switch (dPosition) {
          case 'FL':
            self.position.x -= modelCabinetWidth + cabinetDiffWith / 2;
            self.position.x += 2 * xOffset;
            break;

          case 'FR':
            self.position.x += cabinetDiffWith / 2;
            self.position.x -= 8;
            break;

          case 'C':
            self.position.x += 34;

            self.position.x += cabinetDiffWith / 2;
            self.position.x -= cabinetWidth / 2;
            if (doorPosition == 'FL') self.position.x += 4;
            else if (doorPosition == 'FR') self.position.x -= 4;

            break;

          default:
            break;
        }
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x546464}
        specular={0xffffff}
        shininess={3.4822}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
        map={texture}
        onUpdate={self => (self.needsUpdate = true)}
      />
    </mesh>
  );
}

export function DoorKnop({
  door,
  doorHandle,
  dPosition,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  return (
    <DoorHandleNormal
      door={door}
      doorHandle={doorHandle}
      dPosition={dPosition}
      cabinetWidth={cabinetWidth}
      cabinetDepth={cabinetDepth}
      unselected={unselected}
      xOffset={70}
      zOffset={1.5}
      data={data.doorKnop}
    />
  );
}

export function DoorGlassHole({
  door,
  doorHandle,
  dPosition,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  return (
    <DoorHandleNormal
      door={door}
      doorHandle={doorHandle}
      dPosition={dPosition}
      cabinetWidth={cabinetWidth}
      cabinetDepth={cabinetDepth}
      unselected={unselected}
      xOffset={52}
      zOffset={0}
      data={data.doorGlassHole}
      isGlassHole={true}
    />
  );
}

export function DoorSmallButton({
  door,
  doorHandle,
  dPosition,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  return (
    <DoorHandleNormal
      door={door}
      doorHandle={doorHandle}
      dPosition={dPosition}
      cabinetWidth={cabinetWidth}
      cabinetDepth={cabinetDepth}
      unselected={unselected}
      xOffset={67}
      zOffset={0}
      data={data.doorSmallButton}
    />
  );
}

export function DoorBigButton({
  door,
  doorHandle,
  dPosition,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  return (
    <DoorHandleNormal
      door={door}
      doorHandle={doorHandle}
      dPosition={dPosition}
      cabinetWidth={cabinetWidth}
      cabinetDepth={cabinetDepth}
      unselected={unselected}
      xOffset={70}
      zOffset={0}
      data={data.doorBigButton}
    />
  );
}

function DoorHandleNormal({
  door,
  doorHandle,
  dPosition,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
  xOffset,
  zOffset,
  data,
  isGlassHole = false,
}) {
  let zOffsetForPosition = zOffset;
  if (!door.doorIsInside) zOffsetForPosition -= 6;

  const mesh = useRef();

  const baseData = useMemo(() => compute3DMeshDataAndPosition(data), [data]);

  const textures = useMemo(
    () => ({
      textureAluLoaded: loadTexture(textureAlu),
      textureGalvaLoaded: loadTexture(textureCornerGalva),
    }),
    [textureAlu, textureCornerGalva],
  );

  const materialData = isGlassHole
    ? null
    : getMaterialData(doorHandle, textures);

  const [spring, set] = useSpring(() => ({
    position: getPositionDoorHandle(
      baseData.position,
      cabinetWidth,
      cabinetDepth,
      dPosition,
      xOffset,
      zOffsetForPosition,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      set({
        position: getPositionDoorHandle(
          baseData.position,
          cabinetWidth,
          cabinetDepth,
          dPosition,
          xOffset,
          zOffsetForPosition,
        ),
      });
    }
  }, [cabinetWidth, cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      renderOrder={constantsViewer.ZORDER_HANDLE}
      receiveShadow
      castShadow
      position={spring.position}
    >
      {createReactThreeGeometry(baseData)}
      {isGlassHole ? (
        <meshPhongMaterial
          attach="material"
          color={0x808f87}
          specular={0xffffff}
          depthWrite={false}
          shininess={9.18959}
          opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 0.22}
          transparent
        />
      ) : (
        createReactThreeMaterial(materialData, unselected)
      )}
    </a.mesh>
  );
}

function getPositionDoorHandle(
  basePosition,
  cabinetWidth,
  cabinetDepth,
  dPosition,
  xOffset,
  zOffset,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;

  switch (dPosition) {
    case 'FL':
      return [
        basePosition.x -
          constantsViewer.MODEL_CABINET_WIDTH -
          cabinetDiffWith / 2 +
          2 * xOffset,
        basePosition.y - 4,
        basePosition.z + cabinetDiffDepth / 2 - zOffset,
      ];

    case 'FR':
      return [
        basePosition.x + cabinetDiffWith / 2 - 8,
        basePosition.y - 4,
        basePosition.z + cabinetDiffDepth / 2 - zOffset,
      ];

    default:
      return null;
  }
}
